import type { SentimentDistribution } from '../../data/types';
import { formatPercent } from '../../lib/format';
import styles from './SentimentDistribution.module.css';

interface SentimentDistributionProps {
  distribution: SentimentDistribution;
  title: string;
  caption?: string;
}

interface Segment {
  key: keyof SentimentDistribution;
  label: string;
  className: string;
}

const segments: Segment[] = [
  { key: 'strongly_negative', label: 'Strongly negative', className: 'negStrong' },
  { key: 'negative',          label: 'Negative',          className: 'neg' },
  { key: 'mixed',             label: 'Mixed',             className: 'mixed' },
  { key: 'neutral',           label: 'Neutral',           className: 'neutral' },
  { key: 'positive',          label: 'Positive',          className: 'pos' },
  { key: 'strongly_positive', label: 'Strongly positive', className: 'posStrong' },
];

export function SentimentDistributionBar({ distribution, title, caption }: SentimentDistributionProps) {
  const total = segments.reduce((acc, s) => acc + (distribution[s.key] || 0), 0);
  if (total === 0) return null;

  return (
    <section className={styles.section}>
      <header className={styles.header}>
        <h3 className={styles.title}>{title}</h3>
        <span className={styles.totalNote}>n&nbsp;=&nbsp;{total.toLocaleString('en-US')}</span>
      </header>

      <div className={styles.bar} role="img" aria-label={`${title} distribution`}>
        {segments.map((s) => {
          const value = distribution[s.key] || 0;
          const pct = (value / total) * 100;
          if (pct === 0) return null;
          return (
            <div
              key={s.key}
              className={`${styles.segment} ${styles[s.className]}`}
              style={{ ['--seg' as string]: `${pct}%` }}
              title={`${s.label}: ${value} (${pct.toFixed(0)}%)`}
            />
          );
        })}
      </div>

      <ul className={styles.legend}>
        {segments.map((s) => {
          const value = distribution[s.key] || 0;
          if (value === 0) return null;
          const pct = (value / total) * 100;
          return (
            <li key={s.key} className={styles.legendItem}>
              <span className={`${styles.swatch} ${styles[s.className]}`} aria-hidden="true" />
              <span className={styles.legendLabel}>{s.label}</span>
              <span className={styles.legendValue}>{formatPercent(pct / 100)}</span>
            </li>
          );
        })}
      </ul>

      {caption ? <p className={styles.caption}>{caption}</p> : null}
    </section>
  );
}
